import {
  haloPadPx,
  IBox,
  IExportLabelFeature,
  IExportLabelLine,
  LabelAlign,
  LabelMeasure,
} from './exportLabelTypes'
import { PLAIN_LABEL_PLATE } from './labelPlateSpec'
import { getLabelPlatePadding } from './locationLabelGeometry'

// The live map's plate padding for a label of this size: the same function sizes its 9-patch.
export function platePadding(sizePx: number): { x: number; y: number } {
  const x = getLabelPlatePadding(sizePx)
  return {
    x,
    y: Math.max(PLAIN_LABEL_PLATE.padYPx, x - PLAIN_LABEL_PLATE.radiusPx),
  }
}

export const POSITION_ORDER = [
  'above',
  'upperRight',
  'right',
  'lowerRight',
  'below',
  'lowerLeft',
  'left',
  'upperLeft',
] as const

export type LabelPosition = (typeof POSITION_ORDER)[number]

export interface ILabelCandidate {
  position: LabelPosition
  sizePx: number
  lineHeightPx: number
  box: IBox
  align: LabelAlign
  cost: number
  isShrunk: boolean
  lines?: IExportLabelLine[]
}

const SHRINK_FACTOR = 0.85
const RANK_COST_SPAN = 0.6
const SHRINK_COST = 0.8
// A stack reads best as its name over what it holds; on one line only where two do not fit.
const STACK_ONE_LINE_COST = 0.65
// What a stack holds is told a step quieter than its name: smaller, regular weight.
const STACK_DETAIL_SCALE = 0.8
const STACK_DETAIL_WEIGHT = 400

const ALIGN: Record<LabelPosition, LabelAlign> = {
  above: 'middle',
  upperRight: 'start',
  right: 'start',
  lowerRight: 'start',
  below: 'middle',
  lowerLeft: 'end',
  left: 'end',
  upperLeft: 'end',
}

function boxAt(
  position: LabelPosition,
  around: IBox,
  width: number,
  height: number,
  offset: number,
): IBox {
  const left = around.x
  const right = around.x + around.width
  const top = around.y
  const bottom = around.y + around.height
  const centreX = left + around.width / 2 - width / 2
  const centreY = top + around.height / 2 - height / 2
  switch (position) {
    case 'above':
      return { x: centreX, y: top - offset - height, width, height }
    case 'upperRight':
      return { x: right + offset, y: top - offset - height, width, height }
    case 'right':
      return { x: right + offset, y: centreY, width, height }
    case 'lowerRight':
      return { x: right + offset, y: bottom + offset, width, height }
    case 'below':
      return { x: centreX, y: bottom + offset, width, height }
    case 'lowerLeft':
      return { x: left - offset - width, y: bottom + offset, width, height }
    case 'left':
      return { x: left - offset - width, y: centreY, width, height }
    case 'upperLeft':
      return {
        x: left - offset - width,
        y: top - offset - height,
        width,
        height,
      }
  }
}

export function labelSizesFor(sizePx: number, floorPx: number): number[] {
  const sizes = [sizePx]
  for (
    let size = sizePx * SHRINK_FACTOR;
    size >= floorPx;
    size *= SHRINK_FACTOR
  ) {
    sizes.push(size)
  }
  return sizes
}

interface ILineSpec {
  text: string
  sizePx: number
  lineHeightPx: number
  weight: number
  width: number
  height: number
}

// Each line in its own box inside the block, aligned the way the block is.
function linesIn(
  block: IBox,
  specs: ILineSpec[],
  align: LabelAlign,
): IExportLabelLine[] {
  let top = block.y
  return specs.map(({ text, sizePx, lineHeightPx, weight, width, height }) => {
    const x =
      align === 'start'
        ? block.x
        : align === 'end'
          ? block.x + block.width - width
          : block.x + (block.width - width) / 2
    const box = { x, y: top, width, height }
    top += height
    return { text, sizePx, lineHeightPx, weight, box }
  })
}

// Sorted by cost, and stable, so at equal cost the nearer offset of a position comes first.
export function generateLabelCandidates(
  feature: IExportLabelFeature,
  measure: LabelMeasure,
  opts: { floorPx: number; gapPx: number; canShrink: boolean },
): ILabelCandidate[] {
  const around = feature.icon ?? {
    x: feature.anchor.x,
    y: feature.anchor.y,
    width: 0,
    height: 0,
  }
  const leading = feature.lineHeightPx / feature.sizePx
  const candidates: ILabelCandidate[] = []
  const sizes = opts.canShrink
    ? labelSizesFor(feature.sizePx, opts.floorPx)
    : [feature.sizePx]
  const lineOf = (text: string, sizePx: number, weight: number): ILineSpec => {
    const lineHeightPx = sizePx * leading
    const plate = feature.ink?.isPlated ? platePadding(sizePx) : null
    return {
      text,
      sizePx,
      lineHeightPx,
      weight,
      width:
        measure(text, sizePx, weight, feature.letterSpacingEm) +
        2 * (plate ? plate.x : haloPadPx(sizePx)),
      height: lineHeightPx + (plate ? 2 * plate.y : 0),
    }
  }
  sizes.forEach((sizePx, step) => {
    const offsets = [opts.gapPx, opts.gapPx + sizePx / 2]
    const pushAround = (specs: ILineSpec[], extraCost: number) => {
      const width = Math.max(...specs.map(spec => spec.width))
      const height = specs.reduce((sum, spec) => sum + spec.height, 0)
      POSITION_ORDER.forEach((position, rank) => {
        offsets.forEach(offset => {
          const box = boxAt(position, around, width, height, offset)
          const align = ALIGN[position]
          candidates.push({
            position,
            sizePx,
            lineHeightPx: specs[0].lineHeightPx,
            box,
            align,
            cost:
              (rank * RANK_COST_SPAN) / (POSITION_ORDER.length - 1) +
              step * SHRINK_COST +
              extraCost,
            isShrunk: step > 0,
            lines: specs.length > 1 ? linesIn(box, specs, align) : undefined,
          })
        })
      })
    }
    const { stack } = feature
    if (stack) {
      pushAround(
        [
          lineOf(stack.header, sizePx, feature.weight),
          lineOf(
            stack.details.join(', '),
            Math.max(sizePx * STACK_DETAIL_SCALE, opts.floorPx),
            STACK_DETAIL_WEIGHT,
          ),
        ],
        0,
      )
    }
    pushAround(
      [lineOf(feature.text, sizePx, feature.weight)],
      stack ? STACK_ONE_LINE_COST : 0,
    )
  })
  return candidates.sort((a, b) => a.cost - b.cost)
}
